import t from "@ext/localization/locale/translate";
import ElementGroups from "@ext/markdown/core/element/ElementGroups";
import { claimDropCursor } from "@ext/markdown/elements/dropCursor/dropcursor";
import {
	dropIntoGallery,
	getDropSide,
	isMergeZone,
	mergeIntoGallery,
} from "@ext/markdown/elements/gallery/edit/logic/galleryDrop";
import { CarouselEdgeScroller } from "@ext/markdown/elements/gallery/edit/plugins/carouselDragScroll";
import GalleryDropIndicator, {
	type GalleryDropTarget,
} from "@ext/markdown/elements/gallery/edit/plugins/GalleryDropIndicator";
import { GALLERY_MAX_ITEMS } from "@ext/markdown/elements/gallery/model/gallery";
import { NodeSelection, Plugin } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

interface DropHit {
	overGallery: boolean;
	target: GalleryDropTarget | null;
}

const MISS: DropHit = { overGallery: false, target: null };

const draggedItem = (view: EditorView) => {
	const content = view.dragging?.slice.content;
	const node = content?.childCount === 1 ? content.firstChild : null;
	return node?.type.isInGroup(ElementGroups.galleryItem) ? node : null;
};

const draggedFrom = (view: EditorView) => {
	const { selection } = view.state;
	return selection instanceof NodeSelection && selection.node === draggedItem(view) ? selection.from : null;
};

const visibleRect = (dom: HTMLElement) => {
	const rect = dom.getBoundingClientRect();
	const clip = dom.closest("[data-gallery-items]")?.getBoundingClientRect();
	if (!clip) return rect;
	const left = Math.max(rect.left, clip.left);
	const right = Math.min(rect.right, clip.right);
	return { left, top: rect.top, width: Math.max(right - left, 0), height: rect.height };
};

const resolveDrop = (view: EditorView, event: DragEvent): DropHit => {
	if (!draggedItem(view)) return MISS;
	const hit = view.posAtCoords({ left: event.clientX, top: event.clientY });
	if (!hit || hit.inside < 0) return MISS;

	const { doc } = view.state;
	const node = doc.nodeAt(hit.inside);
	const Inside = doc.resolve(hit.inside);
	const overGallery = node?.type.name === "gallery" || Inside.parent.type.name === "gallery";
	const dom = view.nodeDOM(hit.inside) as HTMLElement | null;
	const source = draggedFrom(view);
	if (!node || !dom || hit.inside === source) return { overGallery, target: null };

	const rect = visibleRect(dom);
	const side = getDropSide(rect, event.clientX, event.clientY);

	if (Inside.parent.type.name === "gallery") {
		const galleryPos = Inside.before();
		const fromThisGallery = source !== null && doc.resolve(source).parent === Inside.parent;
		if (!fromThisGallery && Inside.parent.childCount >= GALLERY_MAX_ITEMS) return { overGallery, target: null };
		const index = Inside.index() + (side === "after" ? 1 : 0);
		return { overGallery, target: { kind: "insert", galleryPos, index, side, rect, isReorder: fromThisGallery } };
	}

	if (node.type.isInGroup(ElementGroups.galleryItem) && isMergeZone(rect, event.clientY))
		return { overGallery, target: { kind: "merge", targetPos: hit.inside, side, rect } };

	return { overGallery, target: null };
};

class GalleryDragController {
	readonly indicator = new GalleryDropIndicator({
		insert: t("editor.gallery.drop-insert"),
		merge: t("editor.gallery.drop-merge"),
	});
	private readonly _scroller: CarouselEdgeScroller;
	private _lastEvent: DragEvent | null = null;

	constructor(private readonly _view: EditorView) {
		this._scroller = new CarouselEdgeScroller(_view.dom, () => this._showIndicator());
		// Capture: the drop cursor listens on the same element later and must see the claim.
		_view.dom.addEventListener("dragover", this._dragover, true);
		_view.dom.addEventListener("dragleave", this._dragleave, true);
		document.addEventListener("dragover", this._scrollCarousel, true);
		document.addEventListener("dragend", this.reset, true);
	}

	reset = () => {
		this._lastEvent = null;
		this.indicator.show(null);
		this._scroller.stop();
	};

	destroy() {
		this._view.dom.removeEventListener("dragover", this._dragover, true);
		this._view.dom.removeEventListener("dragleave", this._dragleave, true);
		document.removeEventListener("dragover", this._scrollCarousel, true);
		document.removeEventListener("dragend", this.reset, true);
		this._scroller.stop();
		this.indicator.destroy();
	}

	private _dragover = (event: DragEvent) => {
		this._lastEvent = event;
		if (this._showIndicator()) claimDropCursor(event);
	};

	private _scrollCarousel = (event: DragEvent) => {
		if (this._view.dragging) this._scroller.track(event);
	};

	private _showIndicator() {
		if (!this._lastEvent) return false;
		const { overGallery, target } = resolveDrop(this._view, this._lastEvent);
		this.indicator.show(target);
		return overGallery || Boolean(target);
	}

	private _dragleave = (event: DragEvent) => {
		if (this._view.dom.contains(event.relatedTarget as Node | null)) return;
		this._lastEvent = null;
		this.indicator.show(null);
	};
}

const galleryDropPlugin = () => {
	let controller: GalleryDragController | null = null;

	return new Plugin({
		view: (view) => {
			controller = new GalleryDragController(view);
			return controller;
		},
		props: {
			handleDrop: (view, event, _slice, moved) => {
				controller?.reset();
				const { overGallery, target } = resolveDrop(view, event);
				const item = draggedItem(view);
				const source = moved ? draggedFrom(view) : null;
				if (!item || (moved && source === null)) return false;
				// The schema does not cap gallery size, so the default drop would push the last item out.
				if (!target) return overGallery;

				view.focus();
				const { tr } = view.state;
				view.dispatch(
					target.kind === "insert"
						? dropIntoGallery(tr, target.galleryPos, target.index, item, source)
						: mergeIntoGallery(tr, target.targetPos, target.side, item, source),
				);
				return true;
			},
		},
	});
};

export default galleryDropPlugin;
